import QuoteDetails from "../quote-details";
import QuoteSummary from "../quote-summary";
import DistrictComparison from "../district-comparison";
import RenegotiateSummary from "../renegotiate-summary";
import SkuAnalysis from "../../sku-ratio-analysis";
import type { MutableRefObject } from "react";
import { sortMonthYearDescending } from "../../../../utils/formatter";
import {
    QuoteSummaryFiltersState,
    QuoteSummaryMode,
    SkuFiltersState,
    NavItem,
    QuoteValue,
} from "../types";
import { getDistrictComparisonBaseSkuLabel } from "../utils/exportDistrictComparisonExcel";

type VendorQuoteAssessmentContentProps = {
    navItem: NavItem;
    filters: {
        commodity: string | null;
        vendor: string[] | null;
        districts: string[] | null;
    };
    isQuoteDetailsMonthOf: boolean;
    benchmark: string;
    monthYear: string;
    quotes: QuoteValue;
    fetchFilters: any;
    vendorData: any;
    quoteDetails: any;
    districtComparisionData: any;
    skuQuoteDetailsFilters: QuoteSummaryFiltersState;
    skuQuoteDetailsToggle: QuoteSummaryMode;
    setSkuQuoteDetailsToggle: (value: QuoteSummaryMode) => void;
    skuQuoteDetails: any;
    skuQuoteDetailsDistrict: string[];
    skuQuoteDetailsVendor: string[];
    skuQuoteDetailsDate: string[];
    skuFilters: SkuFiltersState;
    skuToggle: boolean;
    setSkuToggle: (value: boolean) => void;
    setQuoteToggle: (value: boolean) => void;
    quoteToggle: boolean;
    skuData: any;
    userDetails: any;
    skuRatioDownloadRef: MutableRefObject<(() => void) | null>;
    fetchSkuQuoteDetailsForDownload: () => Promise<any>;
};

const VendorQuoteAssessmentContent = ({
    navItem,
    filters,
    isQuoteDetailsMonthOf,
    benchmark,
    monthYear,
    quotes,
    fetchFilters,
    vendorData,
    quoteDetails,
    districtComparisionData,
    skuQuoteDetailsFilters,
    skuQuoteDetailsToggle,
    setSkuQuoteDetailsToggle,
    skuQuoteDetails,
    skuQuoteDetailsDistrict,
    skuQuoteDetailsVendor,
    skuQuoteDetailsDate,
    skuFilters,
    skuToggle,
    setSkuToggle,
    setQuoteToggle,
    quoteToggle,
    skuData,
    userDetails,
    skuRatioDownloadRef,
    fetchSkuQuoteDetailsForDownload,
}: VendorQuoteAssessmentContentProps) => (
    <div
        className={`bg-[#F1F1F1] grow overflow-auto shrink p-[2%] pr-[5%] flex flex-col flex-nowrap justify-start items-start ${navItem === "sKURatioAnalysis" ? "gap-4" : "gap-6"
            } h-full w-full`}
    >
        {navItem === "renegotiateSummary" && (
            <RenegotiateSummary commodity={filters.commodity ?? ""} data={vendorData} />
        )}

        {navItem === "quoteDetails" && quoteDetails?.data?.length > 0 && (
            <QuoteDetails
                data={quoteDetails}
                vendor={filters.vendor ?? []}
                district={filters.districts ?? []}
                month={fetchFilters?.date?.[0]?.month_year}
                commodity={filters.commodity}
                isMonthOf={isQuoteDetailsMonthOf}
                indexUploaded={
                    filters.commodity === "Copper Wire" ? true : fetchFilters?.indexUploaded
                }
            />
        )}

        {navItem === "quoteSummary" && (
            <QuoteSummary
                skuQuoteDetailsFilters={skuQuoteDetailsFilters}
                commodity={filters.commodity ?? ""}
                vendor={
                    skuQuoteDetailsFilters.vendor.length > 0
                        ? skuQuoteDetailsFilters.vendor[0]
                        : ""
                }
                quotes={quotes ? Number(quotes) : 0}
                district={
                    skuQuoteDetailsFilters.districts.length > 0
                        ? skuQuoteDetailsFilters.districts[0]
                        : ""
                }
                skuQuoteDetailsToggle={skuQuoteDetailsToggle}
                skuData={skuQuoteDetails?.sku_quote_details_data ?? []}
                vendorSummaryData={skuQuoteDetails?.vendorSummary ?? []}
                setSkuQuoteDetailsToggle={setSkuQuoteDetailsToggle}
                selectedDistrict={skuQuoteDetailsDistrict[0] ?? ""}
                selectedVendor={skuQuoteDetailsVendor[0] ?? ""}
                selectedDate={skuQuoteDetailsDate[0] ?? ""}
                totalDistricts={
                    skuQuoteDetails?.vendorSummary
                        ? Object.keys(skuQuoteDetails.vendorSummary).sort((left, right) =>
                            left.localeCompare(right),
                        )
                        : []
                }
                totalVendors={
                    skuQuoteDetails?.vendorSummary
                        ? Object.keys(skuQuoteDetails.vendorSummary).sort((left, right) =>
                            left.localeCompare(right),
                        )
                        : []
                }
                totalDates={
                    skuQuoteDetails?.vendorSummary
                        ? sortMonthYearDescending(Object.keys(skuQuoteDetails.vendorSummary))
                        : []
                }
                fetchAllBenchmarkData={fetchSkuQuoteDetailsForDownload}
            />
        )}

        {navItem === "sKURatioAnalysis" && (
            <SkuAnalysis
                commodity={filters.commodity ?? ""}
                vendor={skuFilters.vendor.length > 0 ? skuFilters.vendor[0] : ""}
                quotes={quotes ? Number(quotes) : 0}
                district={skuFilters.districts.length > 0 ? skuFilters.districts[0] : ""}
                skuToggle={skuToggle}
                skuData={skuData?.ratio_analysis_data ?? []}
                vendorSummaryData={skuData?.vendorSummary ?? []}
                setSkuToggle={setSkuToggle}
                setQuoteToggle={setQuoteToggle}
                quoteToggle={quoteToggle}
                selectedDistrict={skuFilters.districts[0] ?? ""}
                selectedVendor={skuFilters.vendor[0] ?? ""}
                totalDistricts={skuData?.vendorSummary ? Object.keys(skuData.vendorSummary) : []}
                totalVendors={skuData?.vendorSummary ? Object.keys(skuData.vendorSummary) : []}
                downloadExcelRef={skuRatioDownloadRef}
            />
        )}

        {navItem === "districtComparison" && (
            <DistrictComparison
                isShowDotValue={filters.commodity !== "Copper Wire"}
                data={districtComparisionData}
                baseSku={getDistrictComparisonBaseSkuLabel(filters.commodity, benchmark)}
                date={monthYear}
            />
        )}
    </div>
);

export default VendorQuoteAssessmentContent;